/**
 * Typed parsers for the URL query strings that drive each top-level route.
 *
 * Supported URL formats:
 *   /checkout or /order?tokenId=...&country=NZ[&type=onetime][&saveCard=1][&storedPaymentMethodId=tok_x]
 *   SmartHQ Phase A (/checkout): tokenId + country only (always fresh card entry)
 *   SmartHQ Phase B (/order): tokenId + country + optional storedPaymentMethodId (uses saved card)
 *   /save-card?tokenId=...&country=NZ[&type=onetime]
 *   /cards?tokenId=...&country=NZ
 *
 * Each `parse*` returns either a fully-typed payload or `null` when a
 * required field is missing — page components branch on that and render
 * the standard `/status/error` redirect when null.
 */

import type {
  BaseRouteParams,
  CheckoutRouteParams,
  PaymentRouteCommon,
  SaveCardRouteParams,
  SavedCardsRouteParams,
} from '../types/routes';
import { PAYMENT_TYPES, type PaymentType } from '../utils/constants';

function trim(raw: string | null): string | null {
  if (raw === null) return null;
  const v = raw.trim();
  return v.length === 0 ? null : v;
}

function asPaymentType(raw: string | null): PaymentType | null {
  const v = trim(raw)?.toLowerCase() ?? null;
  if (v === null) return PAYMENT_TYPES.ONETIME;
  if (v === PAYMENT_TYPES.ONETIME || v === PAYMENT_TYPES.RECURRING) return v;
  return null;
}

function asBoolean(raw: string | null): boolean {
  const v = trim(raw)?.toLowerCase() ?? '';
  return v === '1' || v === 'true' || v === 'yes';
}

function parseBase(query: URLSearchParams): BaseRouteParams | null {
  const tokenId = trim(query.get('tokenId'));
  if (!tokenId) return null;
  return { tokenId };
}

function parsePaymentCommon(query: URLSearchParams): PaymentRouteCommon | null {
  const base = parseBase(query);
  if (!base) return null;
  const country = trim(query.get('country'))?.toUpperCase() ?? null;
  const type = asPaymentType(query.get('type'));
  if (!country || !type) return null;
  return { ...base, country, type };
}

/**
 * Parse checkout/order params. Supports both styles:
 *   - Standard: tokenId + country + type (required)
 *   - SmartHQ: tokenId + country (type defaults to ONETIME)
 */
export function parseCheckoutParams(query: URLSearchParams): CheckoutRouteParams | null {
  const base = parseBase(query);
  if (!base) return null;
  const country = trim(query.get('country'))?.toUpperCase() ?? null;
  if (!country) return null;

  // Type is optional for SmartHQ URLs; defaults to ONETIME
  const type = asPaymentType(query.get('type'));
  if (!type) return null; // Reject if type is invalid

  const saveCard = asBoolean(query.get('saveCard'));
  const storedPaymentMethodId = trim(query.get('storedPaymentMethodId'));
  const encryptedSecurityCode = trim(query.get('encryptedSecurityCode'));

  // BFF rejects this combination too — fail early on the client.
  if (saveCard && storedPaymentMethodId) return null;

  return { ...base, country, type, saveCard, storedPaymentMethodId, encryptedSecurityCode };
}

export function parseSaveCardParams(query: URLSearchParams): SaveCardRouteParams | null {
  return parsePaymentCommon(query);
}

export function parseSavedCardsParams(query: URLSearchParams): SavedCardsRouteParams | null {
  const base = parseBase(query);
  if (!base) return null;
  const country = trim(query.get('country'))?.toUpperCase() ?? null;
  if (!country) return null;
  return { ...base, country };
}
